import { type ReactNode, createContext, useContext, useEffect } from 'react';

import useMixedState from '@shared/hooks/mixed-state';
import { useComponentDidMount } from '@shared/hooks/react';

import { request } from '@portal/pkg/api';
import { resetKeys } from '@portal/pkg/api/keys';
import type {
	Account,
	Organization,
	SessionResponse,
} from '@portal/pkg/api/types';
import {
	clearToken,
	observeToken,
	readToken,
	writeToken,
} from '@portal/pkg/session/token';
import { storage } from '@portal/pkg/storage/local';

/** `loading` is the stored token being replayed against the API. */
export type SessionStatus = 'loading' | 'anonymous' | 'authenticated';

interface SessionState {
	status: SessionStatus;
	account: Nullable<Account>;
	organization: Nullable<Organization>;
	availableOrganizations: Organization[];
}

export interface Session extends SessionState {
	login: (username: string, password: string) => Promise<void>;
	selectOrganization: (organizationId: number) => Promise<void>;
	logout: () => void;
}

const AnonymousSession: SessionState = {
	status: 'anonymous',
	account: null,
	organization: null,
	availableOrganizations: [],
};

const OrganizationKey = 'session.organization';

/**
 *	The organization a fresh sign-in is scoped to without asking: the one last
 *	chosen in this browser while it is still available, otherwise the only one
 *	there is.
 */
function preferredOrganization(available: Organization[]): Nullable<number> {
	const remembered = Number(storage.get(OrganizationKey, ''));

	if (available.some((organization) => organization.id === remembered)) {
		return remembered;
	}

	return available.length === 1 ? (available[0]?.id ?? null) : null;
}

const SessionContext = createContext<Nullable<Session>>(null);

export function useSession(): Session {
	const session = useContext(SessionContext);

	if (!session) {
		throw new Error('useSession must be used inside a SessionProvider');
	}

	return session;
}

/**
 *	Who is signed in, and which organization they are looking at.
 *
 *	Its own context rather than part of `@shared/providers/application-state`,
 *	which the playbook site mounts too and which `@shared/compositions/navigation`
 *	already reads — neither should see credentials. The lifecycles differ as
 *	well: that one hydrates synchronously from storage, this one by replaying
 *	the stored token against the API.
 *
 *	Only the token is persisted. The account and the organization are read back
 *	from `/api/me` on every load, so a capability revoked since cannot be
 *	served out of storage.
 */
export function SessionProvider({ children }: WithChildren): ReactNode {
	const [state, setState] = useMixedState<SessionState>({
		...AnonymousSession,
		status: 'loading',
	});

	// useComponentDidMount, not useEffect: React's development mount-remount
	// would otherwise ask /api/me twice on every load.
	useComponentDidMount(() => {
		if (!readToken()) {
			setState({ status: 'anonymous' });

			return;
		}

		void hydrate();
	});

	// A 401 from anywhere ends here. The API client clears the token, which
	// broadcasts, which empties the session and drops every cached response;
	// the route guard then sends the person to the login page. Nothing else in
	// the app handles 401.
	useEffect(
		() =>
			observeToken((token) => {
				if (token !== null) {
					return;
				}

				resetKeys();
				setState(AnonymousSession);
			}),
		[]
	);

	async function hydrate(): Promise<void> {
		try {
			const me = await request<SessionResponse>('GET', '/api/me');

			setState({
				status: 'authenticated',
				account: me.account,
				organization: me.organization ?? null,
				availableOrganizations: me.availableOrganizations,
			});
		} catch {
			// A 401 already cleared the token through the listener above. Any
			// other failure still leaves a session nothing can be done with.
			clearToken();
			setState(AnonymousSession);
		}
	}

	async function login(username: string, password: string): Promise<void> {
		const result = await request<{ token: string }>('POST', '/api/auth', {
			payload: { username, password },
			anonymous: true,
		});

		// Signing in replaces whatever was there, including nothing.
		writeToken(result.token, readToken());
		resetKeys();

		// Scoped before the session is published, so nothing sees a signed-in
		// session without an organization and sends the person to the picker.
		// Any failure leaves the picker as the way forward.
		try {
			const me = await request<SessionResponse>('GET', '/api/me');
			const preferred = preferredOrganization(me.availableOrganizations);

			if (!me.organization && preferred !== null) {
				await scopeTo(preferred);
			}
		} catch {}

		await hydrate();
	}

	async function scopeTo(organizationId: number): Promise<void> {
		const result = await request<{ token: string }>(
			'POST',
			'/api/auth/organization',
			{ payload: { organizationId } }
		);

		writeToken(result.token, readToken());
		storage.set(OrganizationKey, String(organizationId));

		// The new token scopes a different organization, so nothing read under
		// the old one may survive.
		resetKeys();
	}

	async function selectOrganization(organizationId: number): Promise<void> {
		await scopeTo(organizationId);

		// Read back rather than taking the organization off this response: the
		// allowed actions the portal renders its buttons from are evaluated
		// against the current user, which the org-less token this request was
		// made with does not carry.
		await hydrate();
	}

	function logout(): void {
		clearToken();
	}

	return (
		<SessionContext.Provider
			value={{ ...state, login, selectOrganization, logout }}>
			{children}
		</SessionContext.Provider>
	);
}
